
import { computed, defineComponent, nextTick, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import moment from "moment";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { hasFeature } from "@/core/helpers/feature";
import { useLuxChat } from "@/modern/composables/useLuxChat";
import LuxAvatar from "@/modern/components/lux/LuxAvatar.vue";
import LuxComposer from "@/modern/components/lux/LuxComposer.vue";
import LuxMessage from "@/modern/components/lux/LuxMessage.vue";
import LuxUpsell from "@/modern/components/lux/LuxUpsell.vue";
import { LuxContextType } from "@/modern/types/lux";

export default defineComponent({
  name: "lux",
  components: { LuxAvatar, LuxComposer, LuxMessage, LuxUpsell },
  setup() {
    const route = useRoute();
    const chat = useLuxChat();

    const allowed = computed(() => hasFeature("lux-ai"));
    // Kill switch do backoffice: o config responde enabled=false
    const disabled = computed(() => chat.config.value?.enabled === false);
    const scroller = ref<HTMLElement | null>(null);
    const composer = ref<InstanceType<typeof LuxComposer> | null>(null);

    /**
     * Contexto vem da URL como `?context=machine:42`, deixando os botões
     * "Pedir ajuda à Lux" espalhados pelo SPA como simples links.
     */
    function parseContext(): { type: LuxContextType; id: number | null } {
      const raw = String(route.query.context ?? "");

      if (!raw.includes(":")) {
        return { type: "general", id: null };
      }

      const [type, id] = raw.split(":");
      const parsed = parseInt(id, 10);

      return {
        type: type as LuxContextType,
        id: Number.isFinite(parsed) ? parsed : null,
      };
    }

    function scrollToBottom() {
      nextTick(() => {
        if (scroller.value) {
          scroller.value.scrollTop = scroller.value.scrollHeight;
        }
      });
    }

    async function onSend(content: string) {
      const failedText = await chat.sendMessage(content);

      // O composable devolve o texto quando o envio falha; recolocamos no campo
      // para o aluno não reescrever.
      if (failedText) {
        composer.value?.restore(failedText);
      }

      scrollToBottom();
    }

    async function newConversation() {
      const { type, id } = parseContext();
      await chat.startConversation(type, id);
      scrollToBottom();
    }

    async function openConversation(uuid: string) {
      await chat.openConversation(uuid);
      scrollToBottom();
    }

    function confirmDelete(uuid: string) {
      Swal.fire({
        title: "Apagar esta conversa?",
        text: "Não dá para desfazer.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Apagar",
        cancelButtonText: "Cancelar",
        buttonsStyling: false,
        customClass: {
          confirmButton: "btn btn-danger",
          cancelButton: "btn btn-light",
        },
      }).then((result) => {
        if (result.isConfirmed) {
          chat.removeConversation(uuid);
        }
      });
    }

    const formatDate = (value: string | null) =>
      value ? moment(value).format("DD/MM HH:mm") : "";

    onMounted(async () => {
      if (!allowed.value) {
        return;
      }

      try {
        await chat.loadConfig();
        if (disabled.value) return;
        await chat.loadConversations();
      } catch (e) {
        return;
      }

      const conversationParam = route.query.conversation;
      const { type, id } = parseContext();

      if (conversationParam) {
        await openConversation(String(conversationParam));
      } else if (type !== "general" || id) {
        // Veio de um botão contextual: já abre uma conversa nova amarrada
        // àquele conteúdo.
        await chat.startConversation(type, id);
      }
    });

    watch(() => chat.messages.value.length, scrollToBottom);

    return {
      ...chat,
      allowed,
      disabled,
      scroller,
      composer,
      onSend,
      newConversation,
      openConversation,
      confirmDelete,
      formatDate,
    };
  },
});
